import React, { useMemo, useState } from 'react';
import { Button, Icon } from '@material-ui/core';
import { CoreEvent } from '@sierralabs/core-ui';
import {
  AssignEntityEvent,
  AssignEntityWarning,
  SiteButtonConfigurator,
  SiteButtonProps,
  SiteComponent,
  SiteComponentConfigProps,
  SiteComponentPlugin,
  SiteComponentProps,
  useAssignEntityComponent,
  useAssignEntityDisabled,
  useSiteContext,
  useSubscribeComponentChanges
} from '@sierralabs/site-ui';

import AppSnackbar from '../common/AppSnackbar';
import DocumentUploadDialog from '../secure-portal/documents/components/DocumentUploadDialog';
import { ReferenceData } from '../secure-portal/documents/Document.interface';
import { useUserContext } from '../user/User.context';

export interface QmsFileUploadButtonProps extends SiteButtonProps {}

export const QmsFileUploadButton: React.FC<
  SiteComponentProps<QmsFileUploadButtonProps>
> = props => {
  const component = useSubscribeComponentChanges(
    QmsFileUploadButtonPlugin,
    props
  );
  const { editing, isHover, hoverProps } = useSiteContext();
  const assignEntity = useAssignEntityComponent(component);
  const { assign, disabled, entityDataId } =
    useAssignEntityDisabled(assignEntity);
  const can = useUserContext().permissions.file;
  const [open, setOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>();
  const classes = ['qms-file-upload-button'];
  if (isHover(component)) {
    classes.push('component-hover');
  }
  const className = classes.join(' ');
  const buttonProps = component.props || {};
  const buttonViewProps = !editing
    ? { ...hoverProps(component), className }
    : undefined;
  const { relations, tableName } = assign;

  const referenceData: ReferenceData = useMemo(() => {
    return {
      id: entityDataId,
      tableName: tableName || '',
      relations: relations || []
    };
  }, [entityDataId, relations, tableName]);

  const button = (
    <Button
      size={buttonProps.size}
      disabled={disabled || can.not.update}
      color={buttonProps.color || 'primary'}
      variant={buttonProps.variant || 'outlined'}
      endIcon={buttonProps.endIcon && <Icon>{buttonProps.endIcon}</Icon>}
      startIcon={buttonProps.startIcon && <Icon>{buttonProps.startIcon}</Icon>}
      {...buttonViewProps}
      onClick={() => !editing && assignEntity?.id && setOpen(true)}
    >
      {buttonProps.text || 'Button'}
    </Button>
  );

  return (
    <SiteComponent plugin={QmsFileUploadButtonPlugin} {...props}>
      {editing ? (
        <div className={className} {...hoverProps(component)}>
          {button}
        </div>
      ) : (
        button
      )}
      <DocumentUploadDialog
        open={open}
        setOpen={setOpen}
        referenceData={referenceData}
        onDismiss={(progress, shouldFetch = true) => {
          const { success } = progress;
          if (success > 0) {
            setSuccessMessage(`Number of files uploaded: ${success}`);
            // refresh assign entity card
            const id = assignEntity?.id;
            if (id && shouldFetch) {
              CoreEvent.dispatch(AssignEntityEvent.Assign, { id });
            }
          }
        }}
      />
      <AppSnackbar
        severity='success'
        open={!!successMessage}
        onClose={() => setSuccessMessage(undefined)}
      >
        {successMessage}
      </AppSnackbar>
    </SiteComponent>
  );
};

export const QmsFileUploadButtonConfigurator: React.FC<
  SiteComponentConfigProps<QmsFileUploadButtonProps>
> = props => {
  return (
    <>
      <AssignEntityWarning component={props.component} />
      <SiteButtonConfigurator hideEvents {...props} />
    </>
  );
};

export const QmsFileUploadButtonPlugin: SiteComponentPlugin<QmsFileUploadButtonProps> =
  {
    name: 'QMS File Upload Button',
    type: 'qms-file-upload-button',
    component: QmsFileUploadButton,
    configurator: QmsFileUploadButtonConfigurator,
    initConfig: component => {
      if (!component.props) component.props = {};
      component.props.color = 'primary';
      component.props.variant = 'outlined';
      component.props.text = 'Upload Files';
      component.props.startIcon = 'cloud_upload';
      return component;
    }
  };
